import { Avatar, Autocomplete, TextField } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import Label from "../../Common/Label";
import "../campaignForm.css";
import { Form, Formik } from "formik";
import ValidationSchema from "../Validations/Form1";
import { getRequest, postRequest } from "../../../api/apiServices";
import apiList from "../../../api/apiList";
import { useDebounce } from "../../../hooks/useDebounce";
import { ApiContext } from "../../../context/VonqApiContext";

interface Form1Props {
  nextStep: () => void;
}

interface JobTitle {
  id: number;
  name: string;
}

interface JobFunction {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
}

interface Industry {
  id: number;
  name: string;
}

const Form1: React.FC<Form1Props> = ({ nextStep }) => {
  const apiContext = useContext(ApiContext);
  const [jobTitleSearchTerm, setJobTitleSearchTerm] = useState<string>("");
  const [locationSearchTerm, setLocationSearchTerm] = useState<string>("");

  const [jobTitle, setJobTitles] = useState<JobTitle[]>([]);
  const [jobFunctions, setJobFunctions] = useState<JobFunction[]>([]);
  const [location, setLocation] = useState<Location[]>([]);
  const [isLoadingJobTitles, setIsLoadingJobTitles] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState(false);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [error, setError] = useState<string>("");

  const [initialValues, setInitialValues] = useState({
    jobTitle: "",
    jobFunction: "",
    location: "",
    industries: "",
  });

  // Separate debounced terms
  const debouncedJobTitleSearchTerm = useDebounce(jobTitleSearchTerm, 500);
  const debouncedLocationSearchTerm = useDebounce(locationSearchTerm, 500);

  // Fetch Job Titles from the API when search term changes

  const fetchJobTitles = async () => {
    setIsLoadingJobTitles(true);
    setError("");
    try {
      const response = await postRequest(apiList.getJobTitle, {
        search: debouncedJobTitleSearchTerm,
      });
      if (response && response.data && response.data.success) {
        setJobTitles(response.data.data || []);
      } else {
        setJobTitles([]);
      }
    } catch (error) {
      setError("Failed to fetch job titles. Please try again later.");
      setJobTitles([]);
    } finally {
      setIsLoadingJobTitles(false);
    }
  };

  const fetchLocation = async () => {
    setIsLoadingLocations(true);
    setError("");
    try {
      const response = await postRequest(apiList.getLocation, {
        search: debouncedLocationSearchTerm,
      });
      if (response && response.data && response.data.success) {
        setLocation(response.data.data || []);
      } else {
        setLocation([]);
      }
    } catch (error) {
      setError("Failed to fetch locations. Please try again later.");
      setLocation([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const fetchJobFunction = async () => {
    try {
      const functionResponse = await getRequest(apiList.getJobFunction);
      if (
        functionResponse &&
        functionResponse.data &&
        functionResponse.data.success
      ) {
        setJobFunctions(functionResponse.data.data || []);
      } else {
        setJobFunctions([]);
      }
    } catch (error) {
      setError("Failed to fetch job functions. Please try again later.");
      setJobFunctions([]);
    }
  };

  const fetchIndustries = async () => {
    try {
      const functionResponse = await getRequest(apiList.getIndustries);
      if (
        functionResponse &&
        functionResponse.data &&
        functionResponse.data.success
      ) {
        setIndustries(functionResponse.data.data || []);
      } else {
        setIndustries([]);
      }
    } catch (error) {
      setError("Failed to fetch job functions. Please try again later.");
      setIndustries([]);
    }
  };

  useEffect(() => {
    if (!debouncedJobTitleSearchTerm.trim()) {
      setJobTitles([]);
      return;
    }
    fetchJobTitles();
  }, [debouncedJobTitleSearchTerm]);

  // Fetch Location from the API when search term changes
  useEffect(() => {
    if (!debouncedLocationSearchTerm.trim()) {
      setLocation([]);
      return;
    }
    fetchLocation();
  }, [debouncedLocationSearchTerm]);

  // Fetch Job Function and industries from the API (once on component mount)
  useEffect(() => {
    fetchJobFunction();
    fetchIndustries();
    // Prefill from localStorage and ApiContext once
    try {
      const stored = localStorage.getItem("CompaignData");
      const parsed: any = stored && stored !== "undefined" && stored !== "null" ? JSON.parse(stored) : {};
      const form1: any = parsed?.Form1Data || {};
      const ctx: any = (apiContext as any)?.selectedValues || {};

      const preJobTitleName = form1?.jobTitle?.name || ctx?.jobTitle || "";
      const preJobFunctionName = form1?.jobFunction?.name || ctx?.jobFunction || "";
      const preLocationName = form1?.location?.name || ctx?.location || "";
      const preIndustryName = form1?.industries?.name || (Array.isArray(ctx?.industries) ? ctx.industries?.[0] : ctx?.industries) || "";
      const prefillJobTitleSearch =
        form1?.jobTitleSearchTerm ||
        form1?.jobTitle?.name ||
        "";

      setInitialValues({
        jobTitle: typeof preJobTitleName === "string" ? preJobTitleName : preJobTitleName?.name || "",
        jobFunction: typeof preJobFunctionName === "string" ? preJobFunctionName : preJobFunctionName?.name || "",
        location: typeof preLocationName === "string" ? preLocationName : preLocationName?.name || "",
        industries: typeof preIndustryName === "string" ? preIndustryName : preIndustryName?.name || "",
      });

      // VONQ job title id/name from prefill; search term matches selected VONQ label
      if (prefillJobTitleSearch) {
        setJobTitleSearchTerm(prefillJobTitleSearch);
      } else if (preJobTitleName) {
        setJobTitleSearchTerm(
          typeof preJobTitleName === "string"
            ? preJobTitleName
            : preJobTitleName?.name || ""
        );
      }
      if (form1?.locationSearchTerm || preLocationName) {
        setLocationSearchTerm(
          form1?.locationSearchTerm ||
            (typeof preLocationName === "string"
              ? preLocationName
              : preLocationName?.name || "")
        );
      }

      if (form1?.jobTitle?.id && form1?.jobTitle?.name) {
        setJobTitles((prev) => {
          const exists = prev.some((item) => item.id === form1.jobTitle.id);
          return exists ? prev : [form1.jobTitle, ...prev];
        });
      }
      if (form1?.location?.id && form1?.location?.name) {
        setLocation((prev) => {
          const exists = prev.some((item) => item.id === form1.location.id);
          return exists ? prev : [form1.location, ...prev];
        });
      }
    } catch {}
  }, []);

  //  Save Form1 data properly into localStorage
  const storeFormDataToLocalStorage = (values: any) => {
    const selectedJobTitle = jobTitle.find(
      (job) => job.name === values.jobTitle
    );
    const selectedJobFunction = jobFunctions.find(
      (func) => func.name === values.jobFunction
    );
    const selectedLocation = location.find(
      (loc) => loc.name === values.location
    );
    const selectedIndustry = industries.find(
      (ind) => ind.name === values.industries
    );

    const form1Data = {
      jobTitleSearchTerm,
      locationSearchTerm,
      jobTitle: selectedJobTitle || null,
      jobFunction: selectedJobFunction || null,
      location: selectedLocation || null,
      industries: selectedIndustry || null,
    };

    const storedData = localStorage.getItem("CompaignData");
    let parsedData = {};

    if (storedData && storedData !== "undefined" && storedData !== "null") {
      try {
        parsedData = JSON.parse(storedData);
      } catch (e) {
        parsedData = {};
      }
    }

    const updatedData = {
      ...parsedData,
      Form1Data: form1Data,
    };

    localStorage.setItem("CompaignData", JSON.stringify(updatedData));
  };

  return (
    <div className="container" id="campaign-container">
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={(values) => {
          storeFormDataToLocalStorage(values);
          nextStep();
        }}
      >
        {({ values, errors, setFieldValue, touched, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <div className="row flex-column gap-3">
              <div className="col d-flex align-items-start justify-content-center flex-column gap-1">
                <div className="d-flex">
                  <Avatar className="avatar">1</Avatar>
                  <Label
                    children="Let's target the right talent"
                    fontSize={"20.94px"}
                    fontWeight={700}
                    color="#273848"
                  />
                </div>
                <Label
                  children="We will use this information to recommend you the best performing channels, so you can target the right candidates."
                  fontWeight={500}
                  fontSize={"12.94px"}
                  color="#273848"
                />
              </div>

              {/* Job Title Section */}
              <div className="col">
                <div className="d-flex align-items-center gap-1">
                <Label
                  children="Job Title"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
                </div>
                <div>
                  <Label
                    children="Enter and select the job title that describes the role (used to select the best performing channels)"
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <Autocomplete
                    options={jobTitle}
                    inputValue={jobTitleSearchTerm}
                    value={
                      jobTitle.find((job) => job.name === values.jobTitle) ||
                      null
                    }
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "reset") return;
                      setJobTitleSearchTerm(newInputValue);
                    }}
                    onChange={(_, selectedJob) => {
                      setFieldValue(
                        "jobTitle",
                        selectedJob ? selectedJob.name : ""
                      );
                      if (selectedJob?.name) {
                        setJobTitleSearchTerm(selectedJob.name);
                      }
                    }}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="e.g., Software Engineer"
                        variant="outlined"
                        size="small"
                        name="jobTitle"
                        error={Boolean(errors.jobTitle && touched.jobTitle)}
                        helperText={touched.jobTitle && errors.jobTitle}
                        fullWidth
                      />
                    )}
                    noOptionsText={
                      isLoadingJobTitles ? "Loading..." : "No job titles found"
                    }
                  />
                </div>
              </div>

              {/* Job Function Section */}
              <div className="col">
                
                <div className="d-flex align-items-center gap-1">
                <Label
                  children="Job Function"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
                </div>
                <div>
                  <Label
                    children="Select a matching job function (used to select the best performing channels)"
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <Autocomplete
                    options={jobFunctions}
                    value={
                      jobFunctions.find(
                        (jobFunction) => jobFunction.name === values.jobFunction
                      ) || null
                    }
                    onChange={(_, selectedJobFunction) => {
                      setFieldValue(
                        "jobFunction",
                        selectedJobFunction ? selectedJobFunction.name : ""
                      );
                    }}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select a job function"
                        variant="outlined"
                        size="small"
                        name="jobFunction"
                        error={Boolean(
                          errors.jobFunction && touched.jobFunction
                        )}
                        helperText={touched.jobFunction && errors.jobFunction}
                        fullWidth
                      />
                    )}
                    noOptionsText="No job functions found"
                  />
                </div>
              </div>

              {/* Location Section */}
              <div className="col">
              <div className="d-flex align-items-center gap-1">
                <Label
                  children="Location"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                />
                <Label
                  children="*"
                  fontWeight={500}
                  fontSize={"14.94px"}
                  color="#FF0000"
                />
                </div>
                <div>
                  <Label
                    children="Enter the location where you would like to look for candidates"
                    fontWeight={500}
                    fontSize={"14px"}
                    color="#4E4E4E"
                    className="mb-1"
                  />
                  <Autocomplete
                    options={location}
                    inputValue={locationSearchTerm}
                    value={
                      location.find((loc) => loc.name === values.location) ||
                      null
                    }
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "reset") return;
                      setLocationSearchTerm(newInputValue);
                    }}
                    onChange={(_, selectedLocation) => {
                      setFieldValue(
                        "location",
                        selectedLocation ? selectedLocation.name : ""
                      );
                      if (selectedLocation?.name) {
                        setLocationSearchTerm(selectedLocation.name);
                      }
                    }}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <>
                        <TextField
                          {...params}
                          placeholder="e.g., New York, NY"
                          variant="outlined"
                          size="small"
                          name="location"
                          error={Boolean(errors.location && touched.location)}
                          helperText={touched.location && errors.location}
                          fullWidth
                        />
                        {error && (
                          <div style={{ color: "red", marginTop: "4px" }}>
                            {error}
                          </div>
                        )}
                      </>
                    )}
                    noOptionsText={
                      isLoadingLocations ? "Loading..." : "No locations found"
                    }
                  />
                </div>
              </div>

              {/* Industries Section */}
              <div className="col">
                <Label
                  children="Industries (Optional)"
                  fontWeight={700}
                  fontSize={"14.94px"}
                  color="#273848"
                />
                <div>
                  <Autocomplete
                    options={industries}
                    value={
                      industries.find(
                        (industry) => industry.name === values.industries
                      ) || null
                    }
                    onChange={(_, selectedIndustry) => {
                      setFieldValue(
                        "industries",
                        selectedIndustry ? selectedIndustry.name : ""
                      );
                    }}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select an industry"
                        variant="outlined"
                        size="small"
                        name="industries"
                        error={Boolean(errors.industries && touched.industries)}
                        helperText={touched.industries && errors.industries}
                        fullWidth
                      />
                    )}
                    noOptionsText={
                      isLoadingIndustries ? "Loading..." : "No industries found"
                    }
                  />
                </div>
              </div>

              {/* Button Section */}
              <div className="col d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 mt-5">
                <button className="continue-btn w-100" type="submit">
                  Next - Target Group
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Form1;
